import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { StatusStep } from './StatusStep';
import { StatusModalState } from '@/hooks/useStatusModal';
import { cn } from '@/lib/utils';

interface StatusModalProps {
  isVisible: boolean;
  state: StatusModalState;
  onRetry?: () => void;
  onDownloadPDF?: () => void;
  onNewOrder?: () => void;
  onClose?: () => void;
}

export const StatusModal: React.FC<StatusModalProps> = ( {
  isVisible,
  state,
  onRetry,
  onDownloadPDF,
  onNewOrder,
  onClose,
} ) => {

  const getMainMessage = () => {
    if ( state.error ) {
      return {
        title: 'Ops! Algo não saiu como esperado',
        subtitle: 'Não se preocupe, seus dados estão seguros. Vamos tentar novamente?',
        emoji: '🤔'
      };
    }

    if ( state.currentStep === 'completed' ) {
      return {
        title: `Pedido #${ state.orderNumber } criado com sucesso!`,
        subtitle: 'Tudo pronto! Você pode baixar o PDF ou criar um novo pedido.',
        emoji: '🎉'
      };
    }

    return {
      title: 'Criando seu pedido...',
      subtitle: 'Aguarde enquanto processamos todas as informações.',
      emoji: '⚡'
    };
  };

  const message = getMainMessage();
  const isProcessing = !state.error && state.currentStep !== 'completed';
  const canClose = !isProcessing;

  const getActionButtons = () => {
    if ( state.error ) {
      return (
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button
            variant="outline"
            onClick={onClose}
            className="min-w-[120px]"
          >
            Cancelar
          </Button>
          {/* <Button 
            onClick={onRetry}
            className="min-w-[120px]"
          >
            Tentar Novamente
          </Button> */}
        </div>
      );
    }

    if ( state.currentStep === 'completed' ) {
      return (
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button
            variant="outline"
            onClick={onNewOrder}
            className="min-w-[120px]"
          >
            Novo Pedido
          </Button>
          <Button
            onClick={onDownloadPDF}
            className="min-w-[120px]"
          >
            Baixar PDF
          </Button>
        </div>
      );
    }

    return null;
  };

  return (
    <Dialog open={isVisible} onOpenChange={canClose ? onClose : undefined}>
      <DialogContent
        className="sm:max-w-2xl max-w-[95vw] p-0"
      >
        <div className="p-6 space-y-6">
          {/* Header */}
          <DialogHeader className="text-center space-y-3">
            <div className="text-4xl mx-auto animate-bounce">
              {message.emoji}
            </div>
            <DialogTitle className="text-xl font-semibold">
              {message.title}
            </DialogTitle>
            <p className="text-muted-foreground text-sm max-w-md mx-auto">
              {message.subtitle}
            </p>
          </DialogHeader>

          {/* Progress Bar */}
          {isProcessing && (
            <div className="space-y-2">
              <Progress
                value={state.progress}
                className="h-2"
              />
              <p className="text-xs text-center text-muted-foreground">
                {state.progress}% concluído
              </p>
            </div>
          )}

          {/* Status Steps Timeline */}
          <div className="py-4">
            <div className="flex items-center justify-between relative">
              {state.steps.map( ( step, index ) => (
                <StatusStep
                  key={step.id}
                  step={step}
                  isLast={index === state.steps.length - 1}
                />
              ) )}
            </div>
          </div>

          {/* Error Details */}
          {state.error && (
            <div className="bg-destructive/5 border border-destructive/20 rounded-lg p-4">
              <p className="text-sm text-destructive/80">
                <strong>Quase lá!</strong> {state.error.message}
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="pt-4">
            {getActionButtons()}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};